import { Box, Button, Container, Grid, Link, Paper, Typography } from '@mui/material';
import PetProfile from 'happypaws-shared/src/Pets/models/PetProfile';
import { PetType } from 'happypaws-shared/src/Pets/models/PetType';
import useAddPets from 'hooks/pets/useAddPets';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdatePet } from '../../hooks/pets/useUpdatePet';
import { useUploadPetPhoto } from '../../hooks/pets/useUploadPetPhoto';
import {
  CatSpecific,
  DogSpecific,
  OtherPetsSpecific,
  PetFormBody,
  PetSummaryRow,
  petTypeSelection,
} from './MyPets/components/PetFormShared';

const AddPetProfile: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: addPets, isPending } = useAddPets();
  const { mutate: uploadPetPhoto } = useUploadPetPhoto();
  const { mutate: updatePet } = useUpdatePet();

  const [petPhotoFiles, setPetPhotoFiles] = useState<(File | null)[]>([null]);
  const [petGalleryFiles, setPetGalleryFiles] = useState<(File | null)[][]>([
    [null, null, null, null],
  ]);

  const [currentPetIndex, setCurrentPetIndex] = useState(0);
  const [pets, setPets] = useState<PetProfile[]>([
    { type: PetType.DOG, petName: '', photoGallery: [] },
  ]);

  const mainPhotoInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const currentPet = pets[currentPetIndex];

  const updateCurrentPet = useCallback(
    (updates: Partial<PetProfile>) => {
      setPets((prev) =>
        prev.map((pet, idx) => (idx === currentPetIndex ? { ...pet, ...updates } : pet))
      );
    },
    [currentPetIndex]
  );

  const handleMainPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Store File for upload later
      setPetPhotoFiles((prev) => {
        const updated = [...prev];
        updated[currentPetIndex] = file;
        return updated;
      });

      // Store blob URL for preview
      const previewUrl = URL.createObjectURL(file);
      updateCurrentPet({ photoUrl: previewUrl });
    }
  };

  const handleGalleryPhotoUpload = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Store File for upload later
      setPetGalleryFiles((prev) => {
        const updated = [...prev];
        const gallery = [...(updated[currentPetIndex] ?? [null, null, null, null])];
        gallery[index] = file;
        updated[currentPetIndex] = gallery;
        return updated;
      });

      // Store blob URL for preview
      const previewUrl = URL.createObjectURL(file);
      const newGallery = [...(currentPet.photoGallery ?? [null, null, null, null])] as (
        | string
        | null
      )[];
      newGallery[index] = previewUrl;
      updateCurrentPet({ photoGallery: newGallery.filter(Boolean) as string[] });
    }
  };

  const handleAddAnotherPet = () => {
    setPets((prev) => [...prev, { type: PetType.DOG, petName: '', photoGallery: [] }]);
    setPetPhotoFiles((prev) => [...prev, null]);
    setPetGalleryFiles((prev) => [...prev, [null, null, null, null]]);
    setCurrentPetIndex(pets.length);
  };

  const handleDeletePet = (index: number) => {
    setPets((prev) => prev.filter((_, i) => i !== index));
    setPetPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setPetGalleryFiles((prev) => prev.filter((_, i) => i !== index));
    setCurrentPetIndex((prev) => {
      if (index < prev) return prev - 1;
      if (index === prev) return Math.max(0, prev - 1);
      return prev;
    });
  };

  const handleNextStep = () => {
    if (!currentPet.petName?.trim()) {
      alert("Please enter your pet's name");
      return;
    }

    addPets(pets, {
      onSuccess: (createdPets) => {
        // Upload photos for each pet that has a file
        const uploadPromises = createdPets.map((pet: PetProfile, index: number) => {
          const promises: Promise<void>[] = [];

          const mainFile = petPhotoFiles[index];
          if (mainFile && pet.id) {
            const p = new Promise<void>((resolve) => {
              uploadPetPhoto(
                { petId: pet.id!, file: mainFile },
                {
                  onSuccess: (photoUrl) => {
                    updatePet({ petId: pet.id!, data: { photoUrl } });
                    resolve();
                  },
                  onError: () => resolve(),
                }
              );
            });
            promises.push(p);
          }

          return Promise.all(promises);
        });

        Promise.all(uploadPromises).then(() => navigate('/dashboard'));
      },
      onError: (error) => console.error('Failed to save pets:', error),
    });
  };

  const petTypeFields = useMemo(() => {
    switch (currentPet.type) {
      case PetType.DOG:
        return <DogSpecific currentPet={currentPet} updateCurrentPet={updateCurrentPet} />;
      case PetType.CAT:
        return <CatSpecific currentPet={currentPet} updateCurrentPet={updateCurrentPet} />;
      case PetType.OTHER:
        return <OtherPetsSpecific currentPet={currentPet} updateCurrentPet={updateCurrentPet} />;
      default:
        return null;
    }
  }, [currentPet, updateCurrentPet]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FAF8F5', py: 4 }}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          sx={{ textAlign: 'center', fontWeight: 700, color: '#178B8E', mb: 1 }}
        >
          Add your Pet Profile
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', color: '#5A5A5A', mb: 4 }}>
          Tell us about your furry (or not so furry) friend!
        </Typography>

        {/* Pet Type Selection */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {petTypeSelection.map(({ type, label, animal }) => (
            <Grid size={{ xs: 12, sm: 4 }} key={type}>
              <Paper
                onClick={() => updateCurrentPet({ type })}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: currentPet.type === type ? '3px solid #178B8E' : '2px solid #E0E0E0',
                  backgroundColor: currentPet.type === type ? 'rgba(23, 139, 142, 0.05)' : 'white',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
                }}
              >
                <img
                  src={animal}
                  alt={label}
                  style={{ width: 125, height: 125, borderRadius: '8px' }}
                />
                <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ p: 4, backgroundColor: 'white' }}>
          <PetFormBody
            currentPet={currentPet}
            updateCurrentPet={updateCurrentPet}
            mainPhotoInputRef={mainPhotoInputRef}
            galleryInputRefs={galleryInputRefs}
            onMainPhotoUpload={handleMainPhotoUpload}
            onGalleryPhotoUpload={handleGalleryPhotoUpload}
            petTypeFields={petTypeFields}
          />

          {/* Pet Summary List */}
          {pets.length > 1 && (
            <Box sx={{ mb: 3 }}>
              {pets.map((pet, index) => (
                <PetSummaryRow
                  key={index}
                  pet={pet}
                  index={index}
                  isActive={index === currentPetIndex}
                  onEdit={() => setCurrentPetIndex(index)}
                  onDelete={() => handleDeletePet(index)}
                />
              ))}
            </Box>
          )}

          {/* Add Another Pet */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Link
              component="button"
              onClick={handleAddAnotherPet}
              sx={{
                color: '#178B8E',
                fontWeight: 600,
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              + Add another pet
            </Link>
            {pets.length > 1 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#5A5A5A' }}>
                {pets.length} pets added
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={isPending}
            onClick={handleNextStep}
            sx={{
              backgroundColor: '#178B8E',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { backgroundColor: '#0F6164' },
            }}
          >
            {isPending ? 'Saving...' : 'Next step'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default AddPetProfile;
